/**
 * App settings view.
 * Provides app-level metadata management, summary, and uninstall controls.
 */

import { useEffect, useMemo, useState } from "react"
import { safeZodResolver, onFormValidationError } from "@/utils/safe-zod-resolver"
import { useForm } from "react-hook-form"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { z } from "zod"
import { useApp } from "@/contexts/app-context"
import { SettingsPageHeader } from "@/components/settings/settings-page-header"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Separator } from "@/components/ui/separator"
import { Skeleton } from "@/components/ui/skeleton"
import { Textarea } from "@/components/ui/textarea"
import { usePageTitle } from "@/hooks/use-page-title"
import { deleteApp, fetchAppStats, updateApp, type AppStats } from "@/services/app-service"
import { AlertTriangle, Loader2, Save, Settings } from "lucide-react"
import { AppDangerZoneSection, AppSummarySection } from "./app-settings-sections"
import { AppUninstallDialog } from "./app-uninstall-dialog"

const APP_ICON_OPTIONS = ["📱", "🤖", "🧠", "⚙️", "🚀", "💬", "📊", "🛠️", "📚", "✨"]

const appSettingsSchema = z.object({
  name: z.string().min(2, "App name must be at least 2 characters").max(100, "App name must not exceed 100 characters").trim(),
  description: z.string().max(500, "Description must not exceed 500 characters").optional(),
  icon: z.string().max(20, "Icon must not exceed 20 characters").optional(),
  category: z.string().max(100, "Category must not exceed 100 characters").optional(),
})

type AppSettingsFormData = z.infer<typeof appSettingsSchema>

function isDefaultWorkspaceApp(name: string, slug: string): boolean {
  const normalizedName = name.trim().toLowerCase()
  const normalizedSlug = slug.trim().toLowerCase()

  return (
    normalizedName === "default" ||
    normalizedName === "untitled app" ||
    normalizedName === "default workspace app" ||
    normalizedSlug === "default" ||
    normalizedSlug === "untitled"
  )
}

export function AppSettingsView() {
  const navigate = useNavigate()
  const { app, isLoading, error, refetch } = useApp()
  const [stats, setStats] = useState<AppStats | null>(null)
  const [isStatsLoading, setIsStatsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isUninstalling, setIsUninstalling] = useState(false)
  const [showUninstallDialog, setShowUninstallDialog] = useState(false)

  const pageTitle = usePageTitle(
    app ? `${app.name} Settings` : "App Settings",
    "Manage this app's identity and lifecycle"
  )

  const isDefaultApp = useMemo(
    () => (app ? isDefaultWorkspaceApp(app.name, app.slug) : false),
    [app]
  )

  const form = useForm<AppSettingsFormData>({
    resolver: safeZodResolver(appSettingsSchema),
    defaultValues: { name: "", description: "", icon: "", category: "" },
    mode: "onChange",
  })

  useEffect(() => {
    if (!app) return

    form.reset({
      name: app.name,
      description: app.description || "",
      icon: app.icon || "",
      category: app.category || "",
    })
  }, [app, form])

  useEffect(() => {
    if (!app) {
      setStats(null)
      setIsStatsLoading(false)
      return
    }

    setIsStatsLoading(true)
    fetchAppStats(app.id, { silent: true })
      .then((data) => setStats(data))
      .catch(() => setStats(null))
      .finally(() => setIsStatsLoading(false))
  }, [app])

  const handleSave = async (data: AppSettingsFormData) => {
    if (!app) return

    try {
      setIsSaving(true)
      await updateApp(app.id, {
        name: data.name,
        description: data.description?.trim() || undefined,
        icon: data.icon?.trim() || undefined,
        category: data.category?.trim() || undefined,
      })
      await refetch()
      toast.success("App settings updated", {
        description: "Your changes have been saved.",
      })
    } catch (saveError) {
      toast.error("Failed to update app settings", {
        description: saveError instanceof Error ? saveError.message : "Please try again later.",
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleUninstall = async () => {
    if (!app) return

    try {
      setIsUninstalling(true)
      await deleteApp(app.id)
      toast.success("App uninstalled", {
        description: `"${app.name}" has been removed from the workspace.`,
      })
      navigate("/app")
    } catch (uninstallError) {
      toast.error("Failed to uninstall app", {
        description: uninstallError instanceof Error ? uninstallError.message : "Please try again later.",
      })
    } finally {
      setIsUninstalling(false)
      setShowUninstallDialog(false)
    }
  }

  if (isLoading) {
    return (
      <>
        {pageTitle}
        <div className="max-w-2xl space-y-6">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-[30rem] w-full rounded-lg" />
          <Skeleton className="h-40 w-full rounded-lg" />
          <Skeleton className="h-36 w-full rounded-lg" />
        </div>
      </>
    )
  }

  if (error || !app) {
    return (
      <>
        {pageTitle}
        <Alert variant="destructive" className="max-w-2xl">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Failed to load app settings</AlertTitle>
          <AlertDescription>{error || "App not found."}</AlertDescription>
        </Alert>
      </>
    )
  }

  return (
    <>
      {pageTitle}
      <div className="max-w-2xl space-y-8">
        <SettingsPageHeader
          icon={Settings}
          title="App Settings"
          description="Manage this app's identity and lifecycle"
        />

        <section className="space-y-4">
          <div className="space-y-1.5">
            <h3 className="text-sm font-medium">General</h3>
            <p className="text-sm text-muted-foreground">
              Update how this app appears across the workspace.
            </p>
          </div>

          <div className="rounded-lg border bg-card p-6">
            <Form {...form}>
              <form className="space-y-6" onSubmit={form.handleSubmit(handleSave, onFormValidationError)}>
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>App Name</FormLabel>
                      <FormControl>
                        <Input {...field} disabled={isSaving} placeholder="Enter app name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          disabled={isSaving}
                          placeholder="Describe what this app is used for"
                          rows={4}
                        />
                      </FormControl>
                      <FormDescription>Shown on app cards and dashboard headers.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="icon"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>App Icon</FormLabel>
                      <FormControl>
                        <div className="space-y-4">
                          <div className="flex items-center gap-4 rounded-lg border bg-muted/20 p-4">
                            <div className="flex h-12 w-12 items-center justify-center rounded-xl border bg-background text-2xl">
                              {field.value || "📱"}
                            </div>
                            <div className="space-y-1">
                              <p className="text-sm font-medium">Selected icon</p>
                              <p className="text-sm text-muted-foreground">
                                Pick an icon that helps identify this app in lists and headers.
                              </p>
                            </div>
                          </div>

                          <div className="flex flex-wrap gap-2">
                            {APP_ICON_OPTIONS.map((iconOption) => (
                              <button
                                key={iconOption}
                                type="button"
                                onClick={() => field.onChange(iconOption)}
                                disabled={isSaving}
                                className={`flex h-11 w-11 items-center justify-center rounded-lg border-2 text-xl transition-colors ${
                                  field.value === iconOption
                                    ? "border-primary bg-primary/10"
                                    : "border-border bg-background hover:border-muted-foreground/40"
                                }`}
                              >
                                {iconOption}
                              </button>
                            ))}
                          </div>
                        </div>
                      </FormControl>
                      <FormDescription>Select one of the preset icons for this app.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Category</FormLabel>
                      <FormControl>
                        <Input {...field} disabled={isSaving} placeholder="Workspace" />
                      </FormControl>
                      <FormDescription>Optional label used to group related apps.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Separator />

                <div className="flex justify-end">
                  <Button type="submit" disabled={!form.formState.isDirty || isSaving}>
                    {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                    Save Changes
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        </section>

        <AppSummarySection app={app} stats={stats} isStatsLoading={isStatsLoading} />
        <AppDangerZoneSection isDefaultApp={isDefaultApp} onUninstallClick={() => setShowUninstallDialog(true)} />
      </div>

      <AppUninstallDialog
        appName={app.name}
        open={showUninstallDialog}
        isUninstalling={isUninstalling}
        onConfirm={handleUninstall}
        onOpenChange={setShowUninstallDialog}
      />
    </>
  )
}

AppSettingsView.displayName = "AppSettingsView"
